import { useCallback, useEffect, useState } from 'react';

import { isContactScreen } from 'components/CallScribe/contactScreen';
import { podTicketUrl } from 'components/CallScribe/podTicketUrl';

import axiosInstance from 'services/axiosInstance';
import { IPodData, IPodDataSimlified } from 'services/pod/types';
import { useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import { PodDataSelector } from 'store/pod';
import { convertSimplifiedToPodData } from 'store/pod/helpers';
import { Integrations, IntegrationsByIndex } from 'constants/integrations';

interface ICrmOption {
  type: number;
  crmId: number;
  crmName: string;
}

const podContactUrl = (
  psa: Integrations | undefined,
  psaId: number,
  contactId: string,
): string | null => {
  switch (psa) {
    case Integrations.Halo:
      return `/PsaContacts/halo/${psaId}/contacts/${Number(contactId)}`;
    case Integrations.ConnectWise:
      return `/ConnectwisePod/${psaId}/contact/${Number(contactId)}`;
    case Integrations.Autotask:
      return `/PsaContacts/autotask/${psaId}/contacts/${Number(contactId)}`;
    default:
      return null;
  }
};

const psaToIndex = (psa: Integrations | undefined): number | null => {
  switch (psa) {
    case Integrations.ConnectWise: return IntegrationsByIndex.ConnectWise;
    case Integrations.Autotask: return IntegrationsByIndex.Autotask;
    case Integrations.Kaseya: return IntegrationsByIndex.Kaseya;
    case Integrations.ServiceNow: return IntegrationsByIndex.ServiceNow;
    case Integrations.Syncro: return IntegrationsByIndex.SyncroMSP;
    case Integrations.Halo: return IntegrationsByIndex.Halo;
    case Integrations.Zendesk: return IntegrationsByIndex.Zendesk;
    case Integrations.FreshService: return IntegrationsByIndex.FreshService;
    case Integrations.SuperOps: return IntegrationsByIndex.SuperOps;
    default: return null;
  }
};

const hasContactData = (data: Record<string, unknown> | null | undefined): boolean => {
  if (!data) return false;
  return Boolean(
    data.ContactName || data.contactName ||
    data.ContactEmail || data.contactEmail ||
    data.ContactPhone || data.contactPhone,
  );
};

const contactPodData = (psa: Integrations | undefined, data: unknown): IPodData =>
  (psa === Integrations.ConnectWise
    ? (data as IPodData)
    : convertSimplifiedToPodData(data as IPodDataSimlified));

const isSameContact = (
  data: Partial<IPodData> | null | undefined,
  contactId: string | null,
): boolean =>
  contactId !== null && data?.ContactId != null && String(data.ContactId) === contactId;

export interface IUseTicketPodDataResult {
  podData: Partial<IPodData>;
  crmId: number | null;
  settled: boolean;
  reload: () => void;
}

export const useTicketPodData = (): IUseTicketPodDataResult => {
  const config = useAppSelector(ConfigurationSelector);
  const sharedPodData = useAppSelector(PodDataSelector);
  const [localPodData, setLocalPodData] = useState<IPodData | null>(null);
  const [resolvedCrmId, setResolvedCrmId] = useState<number | null>(null);
  const [settled, setSettled] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const contactScreen = isContactScreen(config);
  const contactId = config?.contactId ?? null;

  const reload = useCallback(() => { setAttempt(n => n + 1); }, []);

  useEffect(() => {
    setLocalPodData(null);
    setResolvedCrmId(null);
    setSettled(false);
  }, [config?.psaId, config?.psa, config?.ticketId, contactScreen, contactId, attempt]);

  useEffect(() => {
    if (settled || localPodData) return undefined;
    if (!config?.psaId || !config?.psa) return undefined;

    const primaryPsaId = config.psaId;
    const psa = config.psa;
    let cancelled = false;

    if (contactScreen) {
      const contactUrl = contactId ? podContactUrl(psa, primaryPsaId, contactId) : null;
      if (!contactUrl) {
        setSettled(true);
        return undefined;
      }

      const fetchContact = async (): Promise<IPodData | null> => {
        try {
          const res = await axiosInstance.get<unknown>(contactUrl, { validateStatus: () => true });
          if (res.status < 200 || res.status >= 300 || !res.data) return null;
          const data = contactPodData(psa, res.data);
          return hasContactData(data as unknown as Record<string, unknown>) ? data : null;
        } catch {
          return null;
        }
      };

      const loadContact = async (): Promise<void> => {
        const data = await fetchContact();
        if (cancelled) return;
        if (data) {
          setLocalPodData(data);
          setResolvedCrmId(primaryPsaId);
        }
        setSettled(true);
      };

      void loadContact();

      return () => {
        cancelled = true;
      };
    }

    if (!config.ticketId) return undefined;
    const ticketId = config.ticketId;

    const tryUrl = async (url: string): Promise<IPodData | null> => {
      try {
        const res = await axiosInstance.get<IPodData>(url, { validateStatus: () => true });
        if (res.status < 200 || res.status >= 300) return null;
        return hasContactData(res?.data as unknown as Record<string, unknown>) ? res.data : null;
      } catch {
        return null;
      }
    };

    const run = async (): Promise<void> => {
      const primaryUrl = podTicketUrl(psa, primaryPsaId, ticketId);
      if (primaryUrl) {
        const data = await tryUrl(primaryUrl);
        if (cancelled) return;
        if (data) {
          setLocalPodData(data);
          setResolvedCrmId(primaryPsaId);
          setSettled(true);
          return;
        }
      }

      const targetIndex = psaToIndex(psa);
      if (targetIndex === null) {
        setSettled(true);
        return;
      }

      let crmOptions: ICrmOption[] = [];
      try {
        const res = await axiosInstance.get<ICrmOption[]>('/TicketUpdate/getCrmOptionsDetailed', {
          validateStatus: () => true,
        });
        if (res.status >= 200 && res.status < 300) {
          crmOptions = Array.isArray(res?.data) ? res.data : [];
        }
      } catch {
        setSettled(true);
        return;
      }
      if (cancelled) return;

      const sameTypeCrms = crmOptions
        .filter(o => Number(o.type) === targetIndex && o.crmId !== primaryPsaId)
        .map(o => o.crmId);

      for (const crmId of sameTypeCrms) {
        if (cancelled) return;
        const url = podTicketUrl(psa, crmId, ticketId);
        if (!url) continue;
        const data = await tryUrl(url);
        if (cancelled) return;
        if (data) {
          setLocalPodData(data);
          setResolvedCrmId(crmId);
          setSettled(true);
          return;
        }
      }
      setSettled(true);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    settled,
    localPodData,
    config?.psaId,
    config?.psa,
    config?.ticketId,
    contactScreen,
    contactId,
    attempt,
  ]);

  const fallbackPodData: Partial<IPodData> =
    contactScreen && !isSameContact(sharedPodData, contactId) ? {} : sharedPodData;

  return {
    podData: localPodData ?? fallbackPodData,
    crmId: resolvedCrmId ?? config?.psaId ?? null,
    settled,
    reload,
  };
};
